export const SFW_IMAGES_PER_PAGE = 120;
export const SFW_FIRST_IMAGE_NUMBER = 1;
export const SFW_IMAGE_NUMBER_WIDTH = 8;
export const SFW_IMAGES_PATH = 'E6B595104E3411DF98790800200C9A66/media/sfw-images/';

export type SfwImage = {
  filename: string;
  fullSizePath: string;
  number: number;
  thumbnailPath: string;
};

export type SfwImageConfig = {
  sfwImages?: { statesEnabled?: string[]; totalImages?: number };
};

export type SignedSfwImage = { number: number; url: string };

export type SignedSfwPage = {
  images: SignedSfwImage[];
  page: number;
  refreshAfter: number;
  totalPages: number;
};

export type SignedSfwViewerImages = {
  images: SignedSfwImage[];
  number: number;
  refreshAfter: number;
};

export const canViewSfwImages = (user?: { accountNeedsAgeVerification: boolean } | null): boolean =>
  Boolean(user && !user.accountNeedsAgeVerification);

export const isSfwImagesStateEnabled = (
  config: SfwImageConfig,
  location?: { stateprovCode?: string } | null,
): boolean => {
  const statesEnabled = config.sfwImages?.statesEnabled;
  const state = location?.stateprovCode;
  return Array.isArray(statesEnabled) && typeof state === 'string' && state.length > 0 && statesEnabled.includes(state);
};

export const getSfwTotalImages = (config: SfwImageConfig): number => {
  const total = config.sfwImages?.totalImages ?? 0;
  if (!Number.isSafeInteger(total) || total < SFW_FIRST_IMAGE_NUMBER || total > 10 ** SFW_IMAGE_NUMBER_WIDTH - 1) {
    return 0;
  }
  return total;
};

export const getSfwTotalPages = (config: SfwImageConfig): number =>
  Math.ceil(getSfwTotalImages(config) / SFW_IMAGES_PER_PAGE);

export const getSfwImagesForClient = (
  config: SfwImageConfig,
  location?: { stateprovCode?: string } | null,
): { totalImages: number } | undefined => {
  const totalImages = getSfwTotalImages(config);
  return totalImages > 0 && isSfwImagesStateEnabled(config, location) ? { totalImages } : undefined;
};

export const getSfwImage = (number: number): SfwImage => {
  const filename = `${String(number).padStart(SFW_IMAGE_NUMBER_WIDTH, '0')}.jpg`;
  return {
    filename,
    fullSizePath: `${SFW_IMAGES_PATH}${filename}`,
    number,
    thumbnailPath: `${SFW_IMAGES_PATH}thumbs/${filename}`,
  };
};

export const getSfwPage = (config: SfwImageConfig, page: number): SfwImage[] | null => {
  const totalPages = getSfwTotalPages(config);
  if (!Number.isSafeInteger(page) || page < 1 || page > totalPages) return null;

  const first = (page - 1) * SFW_IMAGES_PER_PAGE + SFW_FIRST_IMAGE_NUMBER;
  const length = Math.min(SFW_IMAGES_PER_PAGE, getSfwTotalImages(config) - first + 1);
  return Array.from({ length }, (_, index) => getSfwImage(first + index));
};

export const getSfwViewerImages = (config: SfwImageConfig, number: number): SfwImage[] | null => {
  const totalImages = getSfwTotalImages(config);
  if (!Number.isSafeInteger(number) || number < SFW_FIRST_IMAGE_NUMBER || number > totalImages) return null;

  return [number - 1, number, number + 1]
    .filter((imageNumber) => imageNumber >= SFW_FIRST_IMAGE_NUMBER && imageNumber <= totalImages)
    .map(getSfwImage);
};

export const getSfwPageNumberForImage = (number: number): number => Math.ceil(number / SFW_IMAGES_PER_PAGE);
